import React, { useCallback, useEffect, useState } from "react";
import { LifeBuoy, Loader2, Plus, Send } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const STATUS_ORDER = ["open", "in_progress", "resolved", "closed"];
const PRIORITIES = ["low", "normal", "high", "urgent"];

const statusTone = (s) => ({
    open: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
    in_progress: "bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/30",
    resolved: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
    closed: "bg-slate-500/15 text-slate-600 dark:text-slate-400 border-slate-500/30",
}[s]);

const priorityTone = (p) => ({
    urgent: "bg-destructive/10 text-destructive border-destructive/30",
    high: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
    normal: "bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/30",
    low: "bg-slate-500/15 text-slate-600 dark:text-slate-400 border-slate-500/30",
}[p]);

export default function SupportCenter() {
    const [data, setData] = useState({ results: [], counts: {}, total: 0 });
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("any");
    const [openTicket, setOpenTicket] = useState(null);
    const [reply, setReply] = useState("");
    const [createOpen, setCreateOpen] = useState(false);
    const [form, setForm] = useState({ subject: "", body: "", priority: "normal", requester_email: "" });
    const [submitting, setSubmitting] = useState(false);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const params = {};
            if (filter !== "any") params.status = filter;
            const { data } = await api.get("/admin/support/tickets", { params });
            setData(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => { load(); }, [load]);

    const create = async () => {
        try {
            setSubmitting(true);
            await api.post("/admin/support/tickets", form);
            toast.success("Ticket created.");
            setCreateOpen(false);
            setForm({ subject: "", body: "", priority: "normal", requester_email: "" });
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    const update = async (id, patch) => {
        try {
            await api.patch(`/admin/support/tickets/${id}`, patch);
            toast.success("Ticket updated.");
            load();
            if (openTicket?.id === id) {
                setReply("");
                const updated = data.results.find((t) => t.id === id);
                if (updated) setOpenTicket({ ...updated, ...patch, replies: patch.reply ? [...(updated.replies || []), { by: "you", body: patch.reply, at: new Date().toISOString() }] : updated.replies });
            }
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div data-testid="admin-support-center">
            <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="overline">Command Center</p>
                    <h1 data-testid="dash-page-title" className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                        Support Center
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">Tickets, bug reports, and feature requests.</p>
                </div>
                <button onClick={() => setCreateOpen(true)} className="btn-green text-sm inline-flex items-center gap-1" data-testid="sup-new">
                    <Plus size={13} /> New ticket
                </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                {STATUS_ORDER.map((s) => (
                    <div key={s} className="card-soft p-4">
                        <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{s.replace("_", " ")}</div>
                        <div className="mt-1 font-heading font-bold text-2xl text-foreground">{data.counts[s] || 0}</div>
                    </div>
                ))}
            </div>

            <div className="mb-4 flex items-center gap-2">
                {["any", ...STATUS_ORDER].map((s) => (
                    <button
                        key={s}
                        onClick={() => setFilter(s)}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${filter === s ? "bg-brand-navy text-white border-brand-navy" : "border-border text-muted-foreground hover:text-foreground"}`}
                        data-testid={`sup-filter-${s}`}
                    >
                        {s === "any" ? "All" : s.replace("_", " ")}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>
            ) : data.results.length === 0 ? (
                <div className="card-soft p-10 text-center">
                    <LifeBuoy size={22} className="mx-auto text-muted-foreground" />
                    <div className="mt-2 font-heading font-semibold">No tickets in this bucket.</div>
                </div>
            ) : (
                <div className="space-y-2">
                    {data.results.map((t) => (
                        <button key={t.id} onClick={() => setOpenTicket(t)} className="w-full text-left card-soft p-4 hover:border-emerald-500/40 transition-colors" data-testid={`sup-row-${t.id}`}>
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-heading font-semibold text-foreground truncate">{t.subject}</span>
                                        <Badge className={`text-[10px] border ${statusTone(t.status)}`}>{t.status?.replace("_", " ")}</Badge>
                                        <Badge className={`text-[10px] border ${priorityTone(t.priority)}`}>{t.priority}</Badge>
                                    </div>
                                    <div className="mt-1 text-xs text-muted-foreground line-clamp-2">{t.body}</div>
                                    <div className="mt-1 text-[10px] text-muted-foreground">
                                        {t.requester_email && <>from {t.requester_email} · </>}
                                        updated {t.updated_at ? new Date(t.updated_at).toLocaleString() : "—"}
                                        {t.replies?.length > 0 && <> · {t.replies.length} repl{t.replies.length === 1 ? "y" : "ies"}</>}
                                    </div>
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
            )}

            {/* Ticket detail dialog */}
            <Dialog open={!!openTicket} onOpenChange={(o) => !o && setOpenTicket(null)}>
                <DialogContent className="max-w-2xl" data-testid="sup-detail-dialog">
                    <DialogHeader>
                        <DialogTitle>{openTicket?.subject}</DialogTitle>
                        <DialogDescription>Ticket details and conversation history.</DialogDescription>
                    </DialogHeader>
                    {openTicket && (
                        <div className="space-y-3">
                            <div className="flex items-center gap-2 flex-wrap text-xs">
                                <Badge className={`text-[10px] border ${statusTone(openTicket.status)}`}>{openTicket.status?.replace("_", " ")}</Badge>
                                <Badge className={`text-[10px] border ${priorityTone(openTicket.priority)}`}>{openTicket.priority}</Badge>
                                {openTicket.requester_email && <span className="text-muted-foreground">from {openTicket.requester_email}</span>}
                            </div>
                            <div className="rounded-lg bg-muted p-3 text-sm text-foreground whitespace-pre-wrap">{openTicket.body || "(no body)"}</div>
                            {openTicket.replies?.length > 0 && (
                                <div className="space-y-2">
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Replies</div>
                                    {openTicket.replies.map((r, i) => (
                                        <div key={i} className="rounded-lg border border-border p-3 text-sm">
                                            <div className="text-[10px] text-muted-foreground">{r.by} · {r.at ? new Date(r.at).toLocaleString() : ""}</div>
                                            <div className="mt-1 text-foreground whitespace-pre-wrap">{r.body}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div>
                                <Textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Type a reply…" rows={3} data-testid="sup-reply" />
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <Select value={openTicket.status} onValueChange={(v) => update(openTicket.id, { status: v })}>
                                    <SelectTrigger className="h-9 w-40" data-testid="sup-set-status"><SelectValue /></SelectTrigger>
                                    <SelectContent>{STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                                </Select>
                                <Select value={openTicket.priority} onValueChange={(v) => update(openTicket.id, { priority: v })}>
                                    <SelectTrigger className="h-9 w-32" data-testid="sup-set-priority"><SelectValue /></SelectTrigger>
                                    <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                                </Select>
                                <button
                                    disabled={!reply.trim()}
                                    onClick={() => update(openTicket.id, { reply })}
                                    className="btn-green text-xs px-3 py-2 inline-flex items-center gap-1 ml-auto disabled:opacity-60"
                                    data-testid="sup-send-reply"
                                >
                                    <Send size={12} /> Send reply
                                </button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* New ticket dialog */}
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent data-testid="sup-create-dialog">
                    <DialogHeader>
                        <DialogTitle>New support ticket</DialogTitle>
                        <DialogDescription>File a new ticket to track a support conversation.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <Input placeholder="Subject *" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="sup-form-subject" />
                        <Textarea placeholder="Body" rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="sup-form-body" />
                        <Input placeholder="Requester email (optional)" value={form.requester_email} onChange={(e) => setForm({ ...form, requester_email: e.target.value })} data-testid="sup-form-email" />
                        <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                            <SelectTrigger data-testid="sup-form-priority"><SelectValue /></SelectTrigger>
                            <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <DialogFooter>
                        <button onClick={() => setCreateOpen(false)} className="btn-outline text-sm px-4 py-2">Cancel</button>
                        <button onClick={create} disabled={submitting || !form.subject.trim()} className="btn-green text-sm px-4 py-2 disabled:opacity-60" data-testid="sup-form-submit">
                            {submitting ? <Loader2 size={13} className="animate-spin" /> : "Create"}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
